import { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { TbFilterDown } from "react-icons/tb";
import { useSelector } from "react-redux";

import { BoxCard } from "../../../Components/KPI/BoxCards";
import { axInst } from "../../../Components/Login/Service";

import Today from "../../../Artifacts/Realization/today.svg";
import Live from "../../../Artifacts/Realization/live MTD.svg";
import LastMonth from "../../../Artifacts/Realization/last month.svg";
import ytd from "../../../Artifacts/Realization/ytd.svg";
import nfa from "../../../Artifacts/Realization/NFA.svg";

import RealizationFilter from "../../../Components/Filters/RealizationFilter";
import ModalMuiTable from "../../../Components/Modals/ModalMuiTable.jsx";
import RealizationTreeRow from "../../../Components/Modals/TreeModalRealization.jsx";

/* ---------------- API CALLS ---------------- */
const fetchRealizationCardData = async (body) => {
  try {
    const res = await axInst.post("realization/realization_cards", body);
    return res.data;
  } catch (error) {
    console.error(error);
    return {};
  }
};

const fetchRealizationNfaCardData = async (body) => {
  try {
    const res = await axInst.post("realization/nfa_cards", body);
    return res.data;
  } catch (error) {
    console.error(error);
    return {};
  }
};

export const RealizationKpi = () => {
  const [isFormVisible, setFormVisible] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [showTree, setShowTree] = useState(false);

  const treeRef = useRef(null);

  const toggleForm = () => setFormVisible((p) => !p);
  const openLiveMtdModal = () => setOpenModal(true);
  const closeModal = () => setOpenModal(false);

  const { data: filterdata, submitted } = useSelector(
    (state) => state.filtersRealization,
  );

  const { data: realizationData } = useQuery({
    queryKey: ["realizationCard", submitted],
    queryFn: () => fetchRealizationCardData(filterdata),
    refetchInterval: 300000,
    cacheTime: 0,
    staleTime: 0,
    refetchIntervalInBackground: true,
  });

  const { data: realizationNfaCardData } = useQuery({
    queryKey: ["realizationNfaCard", submitted],
    queryFn: () => fetchRealizationNfaCardData(filterdata),
    refetchInterval: 300000,
    cacheTime: 0,
    staleTime: 0,
    refetchIntervalInBackground: true,
  });

  /* ---------- CLICK OUTSIDE TO CLOSE TREE ---------- */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (treeRef.current && !treeRef.current.contains(e.target)) {
        setShowTree(false);
      }
    };

    if (showTree) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showTree]);

  /* ---------- ESC KEY TO CLOSE TREE ---------- */
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        setShowTree(false);
      }
    };

    if (showTree) {
      window.addEventListener("keydown", handleEsc);
    }

    return () => {
      window.removeEventListener("keydown", handleEsc);
    };
  }, [showTree]);

  return (
    <div className="flex flex-col gap-2">
      {/* Filter Form */}
      {isFormVisible && (
        <RealizationFilter
          isFormVisible={isFormVisible}
          setFormVisible={setFormVisible}
        />
      )}

      {/* MAIN WRAPPER */}
      <div className="flex flex-col lg:flex-row gap-2 w-full">
        {/* KPI SECTION */}
        <div className="w-full lg:w-[95%]">
          <section className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {/* Today */}
            <BoxCard
              darkcolor="bg-[#4F81BA] text-white"
              color="bg-gradient-to-b from-[#3CD0C0] to-[#129BD2] text-white"
              icon={Today}
              title="Today"
              animate
              lobData={[
                { lob: "MOP", number: realizationData?.Today?.MOP || "0 L" },
                {
                  lob: "Discount",
                  number: realizationData?.Today?.Discount || 0,
                },
                {
                  lob: "Realization",
                  number: realizationData?.Today?.Realization || 0,
                },
              ]}
            />

            {/* Live MTD */}
            <BoxCard
              onClick={openLiveMtdModal}
              handPosition="top-2 right-2"
              darkcolor="bg-[#B19246] text-[#D2E7FF]"
              color="bg-gradient-to-b from-[#FFE700] to-[#F9A526] text-white"
              icon={Live}
              title="Live MTD"
              lobData={[
                {
                  lob: "MOP",
                  number: realizationData?.["Live MTD"]?.MOP || "0 L",
                },
                {
                  lob: "Discount",
                  number: realizationData?.["Live MTD"]?.Discount || 0,
                },
                {
                  lob: "Realization",
                  number: realizationData?.["Live MTD"]?.Realization || 0,
                },
              ]}
            />

            {/* Last Month */}
            <BoxCard
              icon={LastMonth}
              darkcolor="bg-[#3C94A4] text-[#D2E7FF]"
              color="bg-gradient-to-b from-[#b983ec] to-[#8463FA] text-white"
              title="Last Month"
              animate
              lobData={[
                {
                  lob: "MOP",
                  number: realizationData?.["Last Month"]?.MOP || "0 L",
                },
                {
                  lob: "Discount",
                  number: realizationData?.["Last Month"]?.Discount || 0,
                },
                {
                  lob: "Realization",
                  number: realizationData?.["Last Month"]?.Realization || 0,
                },
              ]}
            />

            {/* YTD */}
            <BoxCard
              icon={ytd}
              darkcolor="bg-[#3C94A4] text-[#D2E7FF]"
              color="bg-gradient-to-b from-[#fcc574] to-[#BA7715] text-white"
              title="YTD"
              animate
              lobData={[
                { lob: "MOP", number: realizationData?.["YTD"]?.MOP || "0 L" },
                {
                  lob: "Discount",
                  number: realizationData?.["YTD"]?.Discount || 0,
                },
                {
                  lob: "Realization",
                  number: realizationData?.["YTD"]?.Realization || 0,
                },
              ]}
            />

            {/* NFA FULL WIDTH MOBILE */}
            <div className="relative col-span-2 md:col-span-1" ref={treeRef}>
              <BoxCard
                color="bg-gradient-to-b from-[#ee8ae2] to-[#de4492] text-white"
                title="NFA Status"
                icon={nfa}
                onClick={() => setShowTree((p) => !p)}
                lobData={[
                  {
                    lob: "Total NFA Raised",
                    number: realizationNfaCardData?.TOTAL_NFA_RAISED || "0",
                  },
                  {
                    lob: "Total NFA Pending",
                    number: realizationNfaCardData?.NFA_PENDING || "0",
                  },
                ]}
              />
              {showTree && (
                <>
                  {/* BACKDROP */}
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowTree(false)}
                  />

                  {/* ARROW */}
                  <div className="hidden md:block absolute z-[80] top-[61px] right-[120px] pointer-events-none">
                    <svg
                      width="250"
                      height="90"
                      viewBox="0 0 250 90"
                      className="overflow-visible"
                    >
                      <path
                        d="M10 60 H240 V40"
                        stroke="#cbd5e1"
                        strokeWidth="2.5"
                        fill="none"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>

                  {/* TREE MODAL */}
                  <div
                    className="absolute z-[60] right-[30px] md:right-0 top-[100px] 
        transition-all duration-500 ease-out
        animate-[fadeInScale_0.4s_ease]"
                  >
                    <RealizationTreeRow data={realizationNfaCardData} />
                  </div>
                </>
              )}
            </div>
          </section>
        </div>

        {/* FILTER BUTTON */}
        <div
          className="
          w-full lg:w-[5%]
          h-[70px] lg:h-auto
          rounded-lg p-2
          bg-gradient-to-b from-rose-300 to-orange-200
          flex justify-center items-center
          cursor-pointer transition hover:scale-105
        "
          onClick={toggleForm}
        >
          <TbFilterDown className="w-7 h-7 text-[#2A5775]" />
        </div>
      </div>

      {/* MODAL */}
      {openModal && (
        <ModalMuiTable
          open={openModal}
          sliceName="filtersRealization"
          FilterComponent={RealizationFilter}
          heading="Realization Detailed Report"
          apiEndpoint="/realization/realization_report"
          onClose={closeModal}
        />
      )}
    </div>
  );
};
